import { types, flow } from 'mobx-state-tree'
import dayjs from 'dayjs'

import { API } from '../api'
const { model, maybeNull, number, string, boolean } = types

export const DEFAULT_RETIREMENT_INCOME_GOAL_PCT = 80

const Person = model({
  annualIncome: maybeNull(number),
  birthDate: maybeNull(string),
  contactInfoId: maybeNull(number),
  expectedMortalityAge: maybeNull(number),
  filingStatus: maybeNull(number),
  firstName: maybeNull(string),
  gender: maybeNull(string),
  id: maybeNull(number),
  includeSpouse: maybeNull(boolean),
  includeSsi: maybeNull(boolean),
  lastName: maybeNull(string),
  maritalStatus: maybeNull(boolean),
  middleInitial: maybeNull(string),
  otherIncome: maybeNull(number),
  preferredName: maybeNull(string),
  retirementAge: maybeNull(number),
  retirementIncomeGoalPct: maybeNull(number),
  salary: maybeNull(number),
  socSecNum: maybeNull(string),
  spouseId: maybeNull(number),
  strbirthdate: maybeNull(string),
  totalIncome: maybeNull(number),
}).views(self => ({
  get fullName() {
    return `${self.firstName || ''} ${self.lastName || ''}`
  },
  get displayName() {
    const { preferredName, firstName } = self
    return preferredName || firstName || ''
  },
  get fullDisplayName() {
    return `${self.displayName || ''} ${self.lastName || ''}`
  },
  get retirementYear() {
    return dayjs(self.birthDate).year() + self.retirementAge || null
  },
  // Plan-year age: the age the person attains during the current calendar year.
  // The advice engine and the IRS catch-up rules both work in whole plan years,
  // so this is deliberately not the person's age today.
  get age() {
    return self.birthDate && dayjs().year() - dayjs(self.birthDate).year()
  },
  // Age as of today. Use this — not `age` — to decide whether something is
  // still in the future for the person right now; `age` rounds up to the
  // birthday later this year and would call a future date "already reached".
  get ageToday() {
    return self.birthDate && dayjs().diff(dayjs(self.birthDate), 'year')
  },
  get personExists() {
    return self.id !== null
  },
}))

const PersonStore = model({
  person: maybeNull(Person),
})
  .views(self => ({
    get includeSpouseInPlanning() {
      const { maritalStatus, includeSpouse } = self.person
      return maritalStatus && includeSpouse
    },
  }))
  .actions(self => ({
    getPerson: flow(function* () {
      const person = yield API.get('person')
      self.person = person.data
    }),
    updatePerson: flow(function* (values) {
      const person = yield API.patch('person', values)
      self.person = person.data
    }),
  }))

export default PersonStore
